import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { EntityDocumentDataReview } from '@evidentid/tprm-portal-lib/models/dashboard/Entity.model';
import { DocumentDataReviewDocument } from '@evidentid/tprm-portal-lib/models/document-data-review/DocumentDataReviewDocument.model';
import { DocumentDataReviewField } from '@evidentid/tprm-portal-lib/models/document-data-review/DocumentDataReviewField.model';
import { DocumentDataReviewRequirementInstance } from '@evidentid/tprm-portal-lib/models/document-data-review/DocumentDataReviewRequirementInstance.model';
import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementType';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { FieldSchema } from '../dynamic-form/models/FieldSchema.model';
import { UnifiedFieldRecord, UnifiedFieldRecordKey } from '../dynamic-form/models/UnifiedFieldRecord.model';

export interface DocumentDataReviewRequirements {
    gatewayClient: GatewayApiClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): DocumentDataReviewRequirements {
    return store.$app;
}

type FieldPath = string;

export interface DocumentDataReviewFormInstance {
    fields: Record<FieldPath, DocumentDataReviewField | null>;
    hiddenFields: Record<FieldPath, boolean>;
    disabledFields: UnifiedFieldRecord<boolean>;
    valuesBeforeNotFound: Record<FieldPath, any>;
}

type FormId = string;

type EntityId = string;

type DdrId = string;

export type RequirementTypeComponentRefs = Record<TprmRequirementType, any>;

export interface DocumentDataReviewDataStatus {
    status: OperationStatus;
    instances: DocumentDataReviewRequirementInstance[];
    entity: EntityDocumentDataReview | null;
}

export interface RequirementsSchemaStatus {
    status: OperationStatus;
    data: Record<TprmRequirementType, FieldSchema[]>;
}

function createEmptyDocumentDataReviewDataStatus(): DocumentDataReviewDataStatus {
    return {
        status: OperationStatus.uninitialized,
        instances: [],
        entity: null,
    };
}

export interface DocumentDataReviewDocumentsDataStatus {
    status: OperationStatus;
    documents: DocumentDataReviewDocument[] | null;
}

function createEmptyDocumentDataReviewDocumentsDataStatus(): DocumentDataReviewDocumentsDataStatus {
    return {
        status: OperationStatus.uninitialized,
        documents: null,
    };
}

function createEmptyRequirementsSchemaStatus(): RequirementsSchemaStatus {
    return {
        status: OperationStatus.uninitialized,
        data: {},
    };
}

export interface DocumentDataReviewState {
    forms: Record<FormId, DocumentDataReviewFormInstance>;
    ddrData: Record<EntityId, DocumentDataReviewDataStatus>;
    ddrDocuments: Record<EntityId, DocumentDataReviewDocumentsDataStatus>;
    requirementsSchema: Record<string, RequirementsSchemaStatus>;
    requirementTypeComponentRefs: Record<DdrId, RequirementTypeComponentRefs>;
}

export const useDocumentDataReviewStore = defineStore('documentDataReview', {
    state: (): DocumentDataReviewState => ({
        forms: {},
        ddrData: {},
        ddrDocuments: {},
        requirementsSchema: {},
        requirementTypeComponentRefs: {},
    }),
    actions: {
        initializeDdrForm(payload: {
            formId: string;
            fields: Record<FieldPath, DocumentDataReviewField | null>;
            hiddenFields: Record<FieldPath, boolean>;
            disabledFields: UnifiedFieldRecord<boolean>;
            valuesBeforeNotFound: Record<FieldPath, any>;
        }) {
            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    fields: payload.fields,
                    hiddenFields: payload.hiddenFields,
                    disabledFields: payload.disabledFields,
                    valuesBeforeNotFound: payload.valuesBeforeNotFound,
                },
            };
        },
        cleanupForm(payload: { formId: string }) {
        // TODO: double check if this is correct and reactive
            delete this.forms[payload.formId];
        },
        setFields(payload: { formId: string, data: Record<FieldPath, DocumentDataReviewField | null> }) {
            const form = this.forms[payload.formId];
            if (!form) {
                console.error('form not found, make sure to initialize the form first, form id: ', payload.formId);
                return;
            }

            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    ...form,
                    fields: payload.data,
                },
            };
        },
        setHiddenFieldByPath(payload: { formId: string, path: string, value: boolean }) {
            const form = this.forms[payload.formId];
            if (!form) {
                console.error('form not found, make sure to initialize the form first, form id: ', payload.formId);
                return;
            }

            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    ...form,
                    hiddenFields: {
                        ...form.hiddenFields,
                        [payload.path]: payload.value,
                    },
                },
            };
        },
        setHiddenFields(payload: { formId: string, hiddenFields: Record<FieldPath, boolean> }) {
            const form = this.forms[payload.formId];
            if (!form) {
                console.error('form not found, make sure to initialize the form first, form id: ', payload.formId);
                return;
            }

            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    ...form,
                    hiddenFields: {
                        ...form.hiddenFields,
                        ...payload.hiddenFields,
                    },
                },
            };
        },
        setDdrDisabledFieldByPath(payload: {
            formId: string; path: string; value: Record<UnifiedFieldRecordKey, boolean>;
        }) {
            const form = this.forms[payload.formId];
            if (!form) {
                console.error('form not found, make sure to initialize the form first, form id: ', payload.formId);
                return;
            }

            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    ...form,
                    disabledFields: {
                        ...form.disabledFields,
                        [payload.path]: payload.value,
                    },
                },
            };
        },
        setDdrDisabledFields(payload: { formId: string, disabledFields: UnifiedFieldRecord<boolean> }) {
            const form = this.forms[payload.formId];
            if (!form) {
                console.error('form not found, make sure to initialize the form first, form id: ', payload.formId);
                return;
            }

            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    ...form,
                    disabledFields: {
                        ...form.disabledFields,
                        ...payload.disabledFields,
                    },
                },
            };
        },
        setValueBeforeNotFoundByPath(payload: { formId: string, path: FieldPath, value: any }) {
            const form = this.forms[payload.formId];
            if (!form) {
                console.error('form not found, make sure to initialize the form first, form id: ', payload.formId);
                return;
            }

            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    ...form,
                    valuesBeforeNotFound: {
                        ...form.valuesBeforeNotFound,
                        [payload.path]: payload.value,
                    },
                },
            };
        },
        setValuesBeforeNotFound(payload: { formId: string, values: Record<FieldPath, any> }) {
            const form = this.forms[payload.formId];
            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    ...form,
                    valuesBeforeNotFound: {
                        ...form.valuesBeforeNotFound,
                        ...payload.values,
                    },
                },
            };
        },
        setRequirementTypeComponentRef(payload: {
            ddrId: DdrId;
            requirementTypeComponentRef: RequirementTypeComponentRefs;
        }) {
            this.requirementTypeComponentRefs = {
                ...this.requirementTypeComponentRefs,
                [payload.ddrId]: {
                    ...this.requirementTypeComponentRefs[payload.ddrId],
                    ...payload.requirementTypeComponentRef,
                },
            };
        },
        startLoadingDocumentDataReviewData(payload: { entityId: string }) {
            this.ddrData = {
                ...this.ddrData,
                [payload.entityId]: {
                    status: OperationStatus.loading,
                    instances: [],
                    entity: null,
                } as DocumentDataReviewDataStatus,
            };
        },
        finishLoadingDocumentDataReviewData(
            payload: {
                entityId: string;
                instances: DocumentDataReviewRequirementInstance[];
                entity: EntityDocumentDataReview;
            },
        ) {
            this.ddrData = {
                ...this.ddrData,
                [payload.entityId]: {
                    status: OperationStatus.success,
                    instances: payload.instances,
                    entity: payload.entity,
                } as DocumentDataReviewDataStatus,
            };
        },
        failLoadingDocumentDataReviewData(payload: { entityId: string }) {
            this.ddrData = {
                ...this.ddrData,
                [payload.entityId]: {
                    status: OperationStatus.error,
                    instances: [],
                    entity: null,
                } as DocumentDataReviewDataStatus,
            };
        },
        startLoadingDocumentDataReviewDocuments(payload: { entityId: string }) {
            this.ddrDocuments = {
                ...this.ddrDocuments,
                [payload.entityId]: {
                    status: OperationStatus.loading,
                    documents: null,
                } as DocumentDataReviewDocumentsDataStatus,
            };
        },
        finishLoadingDocumentDataReviewDocuments(
            payload: { entityId: string, documents: DocumentDataReviewDocument[] },
        ) {
            this.ddrDocuments = {
                ...this.ddrDocuments,
                [payload.entityId]: {
                    status: OperationStatus.success,
                    documents: payload.documents,
                } as DocumentDataReviewDocumentsDataStatus,
            };
        },
        failLoadingDocumentDataReviewDocuments(payload: { entityId: string }) {
            this.ddrDocuments = {
                ...this.ddrDocuments,
                [payload.entityId]: {
                    status: OperationStatus.error,
                    documents: null,
                } as DocumentDataReviewDocumentsDataStatus,
            };
        },
        startLoadingRequirementsSchema(payload: { rpName: string }) {
            this.requirementsSchema = {
                ...this.requirementsSchema,
                [payload.rpName]: {
                    status: OperationStatus.loading,
                    data: {},
                },
            };
        },
        finishLoadingRequirementsSchema(payload: { rpName: string, data: Record<TprmRequirementType, any> }) {
            this.requirementsSchema = {
                ...this.requirementsSchema,
                [payload.rpName]: {
                    status: OperationStatus.success,
                    data: payload.data,
                },
            };
        },
        failLoadingRequirementsSchema(payload: { rpName: string }) {
            this.requirementsSchema = {
                ...this.requirementsSchema,
                [payload.rpName]: {
                    status: OperationStatus.error,
                    data: {},
                },
            };
        },

        async loadDocumentDataReviewData(payload: { entityId: string, documentDataReviewId: string, rpName: string }) {
            const { entityId, documentDataReviewId, rpName } = payload;
            const currentStatus = this.ddrData[entityId] || createEmptyDocumentDataReviewDataStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingDocumentDataReviewData({ entityId });
            try {
                const { instances, entity } = await appOf(this).gatewayClient.tprmUi().getDocumentDataReviewData(
                    rpName, documentDataReviewId,
                );
                const latestStatus = this.ddrData[entityId] || createEmptyDocumentDataReviewDataStatus();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }

                // Process data
                this.finishLoadingDocumentDataReviewData({ entityId, instances, entity });
            } catch (error) {
                this.failLoadingDocumentDataReviewData({ entityId });
                await getPersistingErrorActions().showError(error);
            }
        },

        async loadDocumentDataReviewDocuments(payload: {
            entityId: string;
            documentDataReviewId: string;
            commonName: string;
        }) {
            const { entityId, documentDataReviewId, commonName } = payload;
            const currentStatus =
                this.ddrDocuments[entityId] || createEmptyDocumentDataReviewDocumentsDataStatus();
            if (currentStatus.status === OperationStatus.loading) {
                return;
            }
            this.startLoadingDocumentDataReviewDocuments({ entityId });
            try {
                const { documents } = await appOf(this).gatewayClient.submissionTprmUi().getDocumentDataReviewDocuments(
                    commonName,
                    documentDataReviewId,
                );
                const latestStatus =
                    this.ddrDocuments[entityId] || createEmptyDocumentDataReviewDocumentsDataStatus();
                if (latestStatus.status !== OperationStatus.loading &&
                latestStatus.status !== OperationStatus.uninitialized) {
                    return;
                }

                // Process data
                this.finishLoadingDocumentDataReviewDocuments({ entityId, documents });
            } catch (error) {
                this.failLoadingDocumentDataReviewDocuments({ entityId });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadRequirementsSchema(payload: { rpName: string, requirements: TprmRequirementType[] }) {
            const { rpName, requirements } = payload;
            this.startLoadingRequirementsSchema({ rpName });
            try {
                const schema = await appOf(this).gatewayClient.tprmUi().getRequirementsSchema(rpName, requirements);
                const currentStatus = this.requirementsSchema[rpName] || createEmptyRequirementsSchemaStatus();
                if (currentStatus.status !== OperationStatus.loading &&
                currentStatus.status !== OperationStatus.uninitialized) {
                    return;
                }
                this.finishLoadingRequirementsSchema({ rpName, data: schema });
            } catch (error) {
                this.failLoadingRequirementsSchema({ rpName });
                await getPersistingErrorActions().showError(error);
            }
        },
    },
});
